import { Link } from 'react-router-dom';
import {
  CloudArrowUpIcon,
  TableCellsIcon,
  DocumentChartBarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import type { TeamRole } from '../../lib/roles';
import { useTeamId } from '../../hooks/useTeamId';
import { useTeamRole } from '../../hooks/useTeamRole';

interface QuickAction {
  label: string;
  to: string;
  icon: typeof CloudArrowUpIcon;
  /** Roles allowed to see this action. Omit to allow all. */
  roles?: TeamRole[];
}

const actions: QuickAction[] = [
  {
    label: 'Upload Content',
    to: '/upload',
    icon: CloudArrowUpIcon,
    // Marketing content upload — viewers can't create, hide it from them.
    roles: ['OWNER', 'COMPLIANCE_OFFICER', 'MARKETING_MANAGER', 'MARKETING_MEMBER'],
  },
  {
    label: 'Validate LAR',
    to: '/hmda/lar-validation',
    icon: TableCellsIcon,
    // HMDA LAR validation is a write op (uploads a file). Hide from VIEWER.
    roles: ['OWNER', 'COMPLIANCE_OFFICER'],
  },
  {
    label: 'Board Report',
    to: '/board-reports',
    icon: DocumentChartBarIcon,
    // Read-only navigation — anyone can view board reports list.
  },
  {
    label: 'View Calendar',
    to: '/calendar',
    icon: CalendarDaysIcon,
    // Read-only navigation — anyone can view the calendar.
  },
];

export default function QuickActions() {
  const { teamId } = useTeamId();
  const { role, isLoading: roleLoading } = useTeamRole(teamId);

  // Filter quick actions by role. Fail closed on load so write actions
  // don't flash for viewers/marketing during the brief role-fetch window.
  const visibleActions = actions.filter((action) => {
    if (!action.roles) return true;
    if (roleLoading || !role) return false;
    return action.roles.includes(role);
  });

  if (visibleActions.length === 0) return null;

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-3">
        {visibleActions.map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.to}
              to={action.to}
              className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700"
            >
              <Icon className="h-5 w-5 text-teal-600" />
              {action.label}
            </Link>
          );
        })}
      </div>
    </div>
  );
}
